/**
 * @file time-picker/subcomponents/period-selector.tsx
 *
 * AM/PM toggle — equivalent to `VerticalPeriodToggle` / `PeriodToggleImpl` in TimePicker.kt.
 *
 * - Hidden when `is24hour = true`
 * - Vertical layout (AM top, PM bottom) — default layout for M3 vertical TimePicker
 * - Border: 1px Outline or gap between AM/PM
 * - Selected: bg-m3-primary-container + bold text
 * - Unselected: transparent + text-m3-on-surface-variant
 *
 * Ported from `VerticalPeriodToggle` + `PeriodToggleImpl` in TimePicker.kt.
 *
 * @see docs/m3/date-and-time-pickers/time-pickers/TimePicker.kt (PeriodToggleImpl)
 */

import type * as React from "react";
import { cn } from "../../../../lib/utils";
import { TP_SIZE } from "../time-picker.tokens";
import type { TimePickerState } from "../time-picker.types";

// ─── Props ─────────────────────────────────────────────────────────────────────

interface PeriodSelectorProps {
	/** Hoisted state — requires `isPm`, `isHourInputValid`, `setIsPm` */
	state: TimePickerState;
	/**
	 * Layout direction.
	 * - "vertical": AM top, PM bottom (used in Vertical TimePicker)
	 * - "horizontal": AM left, PM right (used in Horizontal TimePicker)
	 * @default "vertical"
	 */
	layout?: "vertical" | "horizontal";
	/** Inline style override — used for custom dimensions */
	style?: React.CSSProperties;
	className?: string;
}

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * AM/PM Period Selector.
 *
 * Automatically hidden when `state.is24hour = true`.
 *
 * Implementation following "updated toggle" pattern (isUpdatedTimepickerToggleEnabled = true):
 * - No outer container border
 * - Two distinct buttons with rounded-m3-full shape
 * - Selected: bg-m3-primary-container + bold text
 *
 * @example
 * ```tsx
 * <PeriodSelector state={state} layout="vertical" />
 * ```
 */
export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
	state,
	layout = "vertical",
	style,
	className,
}) => {
	// Completely hidden in 24h format
	if (state.is24hour) return null;

	const { isPm, isHourInputValid, setIsPm } = state;

	const handleAmClick = (): void => {
		// Only update if hour input is valid — per Compose source:
		// onClick = { if (state.isPm && state.isHourInputValid) state.hour -= 12 }
		if (isPm && isHourInputValid) {
			setIsPm(false);
		}
	};

	const handlePmClick = (): void => {
		// onClick = { if (!state.isPm && state.isHourInputValid) state.hour += 12 }
		if (!isPm && isHourInputValid) {
			setIsPm(true);
		}
	};

	const isVertical = layout === "vertical";

	return (
		<fieldset
			aria-label="AM/PM"
			style={{
				width: isVertical
					? TP_SIZE.periodSelectorVerticalWidth
					: TP_SIZE.periodSelectorHorizWidth,
				height: isVertical
					? TP_SIZE.periodSelectorVerticalHeight
					: TP_SIZE.periodSelectorHorizHeight,
				...style,
			}}
			className={cn(
				// Layout
				isVertical ? "flex flex-col" : "flex flex-row",
				"gap-0 overflow-hidden",
				// Container shape (CornerSmall = rounded-m3-sm)
				"rounded-m3-sm",
				// Border (period selector outline — updated toggle uses border on container)
				"border border-m3-outline",
				className,
			)}
		>
			{/* AM Button */}
			<PeriodButton
				label="AM"
				isSelected={!isPm}
				onClick={handleAmClick}
				isFirst
				isVertical={isVertical}
			/>

			{/* PM Button */}
			<PeriodButton
				label="PM"
				isSelected={isPm}
				onClick={handlePmClick}
				isFirst={false}
				isVertical={isVertical}
			/>
		</fieldset>
	);
};

PeriodSelector.displayName = "PeriodSelector";

// ─── Period Button ─────────────────────────────────────────────────────────────

interface PeriodButtonProps {
	label: "AM" | "PM";
	isSelected: boolean;
	onClick: () => void;
	isFirst: boolean;
	isVertical: boolean;
}

const PeriodButton: React.FC<PeriodButtonProps> = ({
	label,
	isSelected,
	onClick,
	isFirst,
	isVertical,
}) => {
	return (
		<button
			type="button"
			aria-pressed={isSelected}
			aria-label={label}
			onClick={onClick}
			className={cn(
				// Layout
				"flex-1 flex items-center justify-center",
				"min-w-0 min-h-0",
				// Typography — TitleMedium equivalent (16px, medium weight)
				"text-[16px] leading-none",
				// Selected state — from PeriodToggleImpl (useUpdatedToggle = true)
				isSelected
					? [
							"bg-m3-tertiary-container text-m3-on-tertiary-container",
							"font-bold",
						]
					: ["bg-transparent text-m3-on-surface-variant", "font-medium"],
				// Divider between buttons via border
				!isFirst && isVertical && "border-t border-m3-outline",
				!isFirst && !isVertical && "border-l border-m3-outline",
				// Interaction
				"cursor-pointer transition-colors transition-m3-fast-effects",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-m3-primary",
			)}
		>
			{label}
		</button>
	);
};
